import { useState, useRef, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth, authedFetch } from '@/lib/auth';
import { useLanguage } from '@/lib/i18n';
import { AdminCommandPalette } from '@/components/admin/AdminCommandPalette';
import LaunchCountdownBanner from '@/components/LaunchCountdownBanner';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import {
  LayoutDashboard, CreditCard, FileText, Clock, Receipt, Settings,
  HelpCircle, LogOut, Users, Activity, Mail, Shield, ShieldCheck, Menu, X,
  Home, ClipboardCheck, ClipboardList, Rocket, Gift, Play, Database,
  LifeBuoy, Wifi, ChevronDown, Lock, Crown, Sparkles, ExternalLink,
  AlertTriangle, Search, Building2, Layers, Bell, BarChart3, Brain, Languages,
  Megaphone, Image as ImageIcon, BookOpen, KeyRound, Send, ListChecks, ShieldAlert, Upload,
  Network, Wallet, Car, Flag, CalendarClock, Gauge, FileBarChart, Headset, Inbox,
} from 'lucide-react';
import { RefreshCw } from 'lucide-react';
import FounderMemberBadge from '@/components/FounderMemberBadge';
import AccountAccessPanel from '@/components/dashboard/AccountAccessPanel';

// Friendly names for the feature pages the onboarding gate bounces a customer
// away from — used to tell them exactly what unlocks after MEISAI setup.
const LOCKED_FEATURE_LABELS = {
  '/dashboard/reports':         { en: 'Reports',           ja: 'レポート' },
  '/dashboard/reports/history': { en: 'Report History',    ja: 'レポート履歴' },
  '/dashboard/reports/delivery':{ en: 'Delivery Settings', ja: '配信設定' },
  '/dashboard/schedules':       { en: 'Scheduled Reports', ja: '予約レポート' },
  '/dashboard/custom-reports':  { en: 'Custom Reports',    ja: 'カスタム期間レポート' },
  '/dashboard/reimbursement':   { en: 'Reimbursement',     ja: '経費精算レポート' },
  '/dashboard/cards':           { en: 'ETC Cards',         ja: 'ETCカード' },
  '/dashboard/card-mapping':    { en: 'Card Setup & Mapping', ja: 'ETCカード設定・紐付け' },
  '/dashboard/card-recipients': { en: 'Recipients', ja: '配信先（受信者）' },
  '/dashboard/recipients':      { en: 'Recipients', ja: '配信先（受信者）' },
  '/dashboard/card-registration-status': { en: 'Card Registration Status', ja: 'カード登録状況' },
  '/dashboard/meisai-health':   { en: 'MEISAI Connection', ja: 'MEISAI接続状況' },
  '/dashboard/spend-alerts':    { en: 'Smart Alerts',      ja: 'スマートアラート' },
  '/dashboard/vehicle-monitoring': { en: 'Vehicle Monitoring', ja: '車両モニタリング' },
  '/dashboard/family-authorized-vehicles': { en: 'Family Authorized Vehicles', ja: '家族登録車両' },
};

// Dashboard routes gated by PLAN/ROLE entitlement (mirrors the <EntitlementGate>
// wrappers in App.js). When an onboarding-incomplete customer lands on one of
// these AND their plan cannot access it, the onboarding redirect must DEFER to
// <RestrictedAccess> — bouncing them to the wizard would be a lie (finishing
// MEISAI setup will never unlock a feature their plan doesn't include).
const ROUTE_FEATURE_MAP = [
  ['/dashboard/custom-reports', 'custom_date_reports'],
  ['/dashboard/reimbursement', 'reimbursement_reports'],
  ['/dashboard/vehicle-monitoring', 'vehicle_monitoring'],
  ['/dashboard/meisai-health', 'meisai_connection_health'],
  ['/dashboard/ai-review', 'ai_pattern_review'],
  ['/dashboard/manager-review', 'manager_review_queue'],
  ['/dashboard/spend-alerts', 'smart_spend_alerts'],
  ['/dashboard/family-authorized-vehicles', 'family_vehicle_list'],
  ['/dashboard/business-suite', 'business_suite_access'],
];
import BusinessTrackNudge from '@/components/dashboard/BusinessTrackNudge';
// Iter 311 — TrialExpiryBanner + BillingStatusBanner intentionally not
// imported during the early-access launch phase. They remain in the
// repo for the eventual paid-plan re-enable.
import ServiceErrorBanner from '@/components/dashboard/ServiceErrorBanner';
import { useEntitlements } from '@/lib/entitlements';
import { planContextLabel } from '@/lib/planContext';
import AdminThemeSwitcher from '@/components/admin/AdminThemeSwitcher';
import JecNojoJtrSyncPill from '@/components/admin/JecNojoJtrSyncPill';
import EnvironmentRibbon from '@/components/EnvironmentRibbon';
import { SystemHealthBadge } from '@/components/admin/SystemHealthBadge';
import { ProductionGuardProvider } from '@/components/ProductionActionGuard';
import { GlobalPausedBanner } from '@/components/admin/OperatorControls';
import SelfRescueInboxPill from '@/components/admin/SelfRescueInboxPill';
import SetupProgressPill from '@/components/dashboard/SetupProgressPill';
import PlanChip from '@/components/dashboard/PlanChip';
import PageHelp from '@/components/help/PageHelp';
import NavSidebar from '@/components/layout/nav/NavSidebar';

/**
 * Resolve current pathname → help content key.
 * Order matters: most specific routes first, prefix matches last.
 * Anything not matched falls back to '_default' inside PageHelp.
 */
const PATH_TO_HELP_KEY = [
  ['/admin/mission-control', 'mission-control'],
  ['/admin/automation', 'automation-center'],
  ['/admin/customers', 'customers'],
  ['/admin/data-health', 'data-health'],
  ['/admin/visitors', 'visitors'],
  ['/admin/business-applications', 'business-applications'],
  ['/admin/billing-readiness', 'billing-readiness'],
  ['/admin/beta-operations', 'beta-operations'],
  ['/admin/email-templates', 'email-templates'],
  ['/admin/og-preview', 'og-preview'],
  ['/admin/exports', 'exports'],
  ['/admin/reports', 'reports'],
  ['/admin/report-failures', 'report-failures'],
  ['/admin/report-simulator', 'report-simulator'],
  ['/admin/quarantine', 'quarantine'],
  ['/admin/security-events', 'security-events'],
  ['/admin/security', 'security-cockpit'],
  ['/admin/support-messages', 'support-messages'],
  ['/admin/federation', 'federation'],
  ['/admin/fraud-watch', 'fraud-watch'],
  ['/admin/meisai-monitor', 'meisai-monitor'],
  ['/admin/org-operations', 'org-operations'],
  ['/admin/jobs', 'job-monitor'],
  ['/admin/audit', 'audit-logs'],
  ['/admin/export-audit', 'export-audit'],
  ['/admin/emails', 'email-logs'],
  ['/admin/host-tools', 'host-tools'],
  ['/admin/homepage-media', 'homepage-media'],
  ['/admin/testimonial-translations', 'testimonial-translations'],
  ['/admin/closed-accounts', 'closed-accounts'],
  ['/admin/diagnostic', 'diagnostic-360'],
  ['/admin/launch-checklist', 'launch-checklist'],
  ['/admin/settings', 'admin-settings'],
  ['/admin/phase-b-live', 'phase-b-live'],
  ['/admin/meisai-live-accounts', 'meisai-live-accounts'],
  ['/admin', 'admin-overview'],
  ['/dashboard/billing', 'customer-billing'],
  ['/dashboard/cards', 'customer-cards'],
  ['/dashboard/reports', 'customer-reports'],
  ['/dashboard/support', 'customer-support'],
  ['/dashboard', 'customer-dashboard'],
];
function resolveHelpKey(pathname) {
  for (const [prefix, key] of PATH_TO_HELP_KEY) {
    if (pathname === prefix || pathname.startsWith(prefix + '/')) return key;
  }
  return null;
}

const API = process.env.REACT_APP_BACKEND_URL;

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  // ── Persisted navigation control ─────────────────────────────────────
  // railExpanded — NavSidebar shows labels (expanded accordion, 288px) vs
  // icon-only (compact, 80px + popovers).  localStorage: jtr_nav_rail_expanded
  // New-user default: EXPANDED (labels visible = easier to understand). An
  // existing user's saved preference is always respected — only a genuinely
  // absent key falls back to the expanded default (never force-reset).
  const [railExpanded, setRailExpandedState] = useState(() => {
    try {
      const v = localStorage.getItem('jtr_nav_rail_expanded');
      return v === null ? true : v === 'true';
    } catch { return true; }
  });
  const setRailExpanded = (next) => {
    setRailExpandedState((prev) => {
      const val = typeof next === 'function' ? next(prev) : next;
      try { localStorage.setItem('jtr_nav_rail_expanded', String(val)); } catch { /* ignore */ }
      return val;
    });
  };
  const [mobileOpen, setMobileOpen] = useState(false);
  const drawerRef = useRef(null);
  const menuBtnRef = useRef(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  // Hide the global mobile bottom bar + lock page scroll while the nav drawer is open.
  useEffect(() => {
    document.body.classList.toggle('drawer-open', mobileOpen);
    return () => document.body.classList.remove('drawer-open');
  }, [mobileOpen]);

  // Drawer accessibility: focus trap, Escape to close, restore focus on close.
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const drawer = drawerRef.current;
    const focusables = () => drawer
      ? [...drawer.querySelectorAll('a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])')]
        .filter((el) => el.offsetParent !== null)
      : [];
    const t = setTimeout(() => { (focusables()[0] || drawer)?.focus(); }, 30);
    const onKey = (e) => {
      if (e.key === 'Escape') { setMobileOpen(false); return; }
      if (e.key !== 'Tab') return;
      const f = focusables();
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKey);
      menuBtnRef.current?.focus();
    };
  }, [mobileOpen]);

  const [accessPanel, setAccessPanel] = useState(null); // 'track' | 'plan' | 'access' | null
  const [subscription, setSubscription] = useState(null);
  const [meisaiStatus, setMeisaiStatus] = useState(null);
  const [cardsCount, setCardsCount] = useState(0);
  const userMenuRef = useRef(null);
  const navShellRef = useRef(null);
  // Iter — profile dropdown: desktop opens on HOVER (AfterShip-style), with a
  // short close delay so moving the cursor from the avatar into the menu
  // doesn't make it flicker shut. Click/tap still toggles for touch devices.
  const userMenuCloseTimer = useRef(null);
  const isHoverCapable = () => {
    try { return window.matchMedia('(hover: hover) and (pointer: fine)').matches; } catch { return false; }
  };
  const openUserMenu = () => {
    if (userMenuCloseTimer.current) { clearTimeout(userMenuCloseTimer.current); userMenuCloseTimer.current = null; }
    setUserMenuOpen(true);
  };
  const scheduleCloseUserMenu = () => {
    if (userMenuCloseTimer.current) clearTimeout(userMenuCloseTimer.current);
    userMenuCloseTimer.current = setTimeout(() => setUserMenuOpen(false), 180);
  };

  const isAdmin = location.pathname.startsWith('/admin');
  const ja = lang === 'ja';

  const meisaiConnected = !!meisaiStatus?.connected;
  const hasCard = cardsCount > 0;
  const onboardingComplete = meisaiConnected && hasCard;

  useEffect(() => {
    if (!user || isAdmin) return;
    let cancelled = false;
    Promise.all([
      authedFetch(`${API}/api/user/subscription`, { credentials: 'include' }).then(r => r.ok ? r.json() : null).catch(() => null),
      authedFetch(`${API}/api/user/meisai/status`, { credentials: 'include' }).then(r => r.ok ? r.json() : null).catch(() => null),
      authedFetch(`${API}/api/cards`, { credentials: 'include' }).then(r => r.ok ? r.json() : []).catch(() => []),
    ]).then(([sub, meisai, cards]) => {
      if (cancelled) return;
      setSubscription(sub);
      setMeisaiStatus(meisai);
      setCardsCount(Array.isArray(cards) ? cards.length : 0);
    });
    return () => { cancelled = true; };
    // Perf — fetch once per session (on user/admin change). While onboarding
    // is still incomplete we ALSO refetch on navigation so the gate can detect
    // completion; once complete the dep freezes to a constant so navigating
    // between pages no longer triggers 3 API calls per click.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAdmin, onboardingComplete ? '_done' : location.pathname]);

  // Perf — the authenticated app shell (every /dashboard + /admin page) is
  // dense and data-heavy. PostHog session replay (rrweb) serialises the whole
  // DOM continuously and was the main source of dashboard jank (recorder
  // setInterval handlers + forced reflow). Direct app loads already skip the
  // recorder (see index.html init gate), but a user who arrived from a
  // marketing page initialised PostHog with recording ON. Stop it as soon as
  // the app shell mounts so operational screens are never recorded and the
  // main thread stays free. (No-op when PostHog/recording isn't present.)
  useEffect(() => {
    let tries = 0;
    const id = setInterval(() => {
      tries += 1;
      try {
        const ph = window.posthog;
        if (ph && typeof ph.stopSessionRecording === 'function') {
          ph.stopSessionRecording();
          clearInterval(id);
        }
      } catch { /* posthog not ready / blocked — ignore */ }
      if (tries > 20) clearInterval(id);
    }, 500);
    return () => clearInterval(id);
  }, []);


  // Iter 394b — Routes that MUST stay reachable even mid-onboarding.
  // These are universal escape hatches (settings, support, account
  // close, help/privacy/profile). Without this allowlist the redirect
  // gate traps the customer on the wizard with no way to reach support
  // if something goes wrong during setup.
  const ONBOARDING_ALLOWED_PATHS = [
    '/dashboard/settings',
    '/dashboard/support',
    '/dashboard/help',
    '/dashboard/faq',
    '/dashboard/profile',
    '/dashboard/privacy',
    '/dashboard/account',
    '/dashboard/notifications',
    '/dashboard/close-account',
    '/dashboard/security',
    '/dashboard/preferences',
    // PM66 — a customer whose MEISAI connection broke (reconnect_required)
    // is technically "not onboarded" (no live connection) but MUST be able
    // to reach the self-diagnosis page and the secure reconnect form —
    // those pages are exactly how they self-recover. Without this they'd be
    // bounced back to the wizard, defeating the whole self-rescue flow.
    '/dashboard/self-rescue',
    '/dashboard/meisai-vault',
  ];
  const isAllowedDuringOnboarding = ONBOARDING_ALLOWED_PATHS.some(
    (p) => location.pathname === p || location.pathname.startsWith(p + '/')
  );

  // Plan/role entitlement — resolved here (before the onboarding gate) so a
  // plan-locked route can render <RestrictedAccess> instead of being bounced
  // to the wizard. Longest-prefix match against ROUTE_FEATURE_MAP.
  const ent = useEntitlements();
  const lockedFeatureForPath = (() => {
    const match = ROUTE_FEATURE_MAP
      .filter(([p]) => location.pathname === p || location.pathname.startsWith(p + '/'))
      .sort((a, b) => b[0].length - a[0].length)[0];
    return match ? match[1] : null;
  })();
  const isPlanLockedRoute = !!lockedFeatureForPath
    && ent.ready && !ent.is_admin && !ent.can(lockedFeatureForPath);

  // Iter 394b — Track whether the redirect gate is still "deciding"
  // (i.e. data is in-flight OR a redirect is about to fire). When true
  // we render a lightweight loader instead of the full Outlet so the
  // customer doesn't see the old Overview flash for a frame before
  // bouncing to the wizard.
  const dataLoaded = subscription !== null || meisaiStatus !== null;
  // On any non-allowlisted /dashboard/* page (other than /onboarding
  // itself), suppress the Outlet until we KNOW whether the customer
  // is already onboarded. This kills the "Overview flashes for one
  // frame, then bounces to wizard" flicker the customer reported.
  const onGatedDashboardPath = !isAdmin
    && location.pathname.startsWith('/dashboard')
    && location.pathname !== '/dashboard/onboarding'
    && !isAllowedDuringOnboarding
    && !isPlanLockedRoute;
  const willRedirectToWizard = onGatedDashboardPath && (
    !dataLoaded || (dataLoaded && !onboardingComplete)
  );

  // ── Iter 394 — Force re-onboarding gate (rebuilt). ────────────────────
  // Vault state is the ONLY canonical signal. If the customer doesn't
  // have a live MEISAI connection AND at least one card, every dashboard
  // route bounces them straight to /dashboard/onboarding (the wizard).
  // Exception: paths in ONBOARDING_ALLOWED_PATHS (settings, support,
  // help, profile, privacy, close-account, etc.) stay reachable so the
  // customer always has an escape hatch.
  useEffect(() => {
    if (!user || isAdmin) return;
    // Skip while async data is still in-flight.
    if (subscription === null && meisaiStatus === null) return;
    if (onboardingComplete) return;
    if (location.pathname === '/dashboard/onboarding') return;
    if (!location.pathname.startsWith('/dashboard')) return;
    if (isAllowedDuringOnboarding) return;
    // A route the customer's PLAN can't access renders <RestrictedAccess> —
    // don't bounce them to the wizard for something setup won't unlock.
    if (isPlanLockedRoute) return;
    // Tell the customer WHY they were bounced — "<Feature> unlocks after setup"
    // — so the redirect to the wizard never feels like a silent failure.
    const lockedLabel = LOCKED_FEATURE_LABELS[location.pathname];
    if (lockedLabel) {
      const name = ja ? lockedLabel.ja : lockedLabel.en;
      toast.info(
        ja
          ? `${name}は、MEISAIセットアップの完了後にご利用いただけます。`
          : `${name} unlocks once your MEISAI setup is complete.`,
        { id: `locked-${location.pathname}` },
      );
    }
    navigate('/dashboard/onboarding', { replace: true });
  }, [user, isAdmin, subscription, meisaiStatus, onboardingComplete, location.pathname, isAllowedDuringOnboarding, isPlanLockedRoute, navigate]);
  // ───────────────────────────────────────────────────────────────────────

  const isActive = (path) => {
    if (path === '/dashboard' || path === '/admin') return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const planType = subscription?.plan_type;
  const trialDays = subscription?.trial_days_remaining;
  const isPendingTrial = planType === 'pending_trial';
  const isActiveTrial = planType === 'premium_trial';
  const isPaidPlan = planType === 'premium_monthly' || planType === 'premium_annual' || user?.plan === 'daily_plus';

  // planPill (mobile chip) is computed after entitlements resolve so it can
  // reflect the Free Weekly tier — see below, just after `ent`.

  const handleLogout = async () => { await logout(); navigate('/'); };
  const initials = (user?.name || user?.email || '?').slice(0, 1).toUpperCase();
  const displayName = user?.name || user?.email?.split('@')[0] || '';

  // Customer nav groups
  // Free Weekly tier — drives the topbar badge + mobile pill so a downgraded
  // user never sees "Premium / Early Access" framing. Mirrors FreeWeeklyBanner.
  const isFreeWeeklyTier = ent.ready && !ent.is_admin && ent.plan_group === 'PERSONAL_WEEKLY_FREE';
  // Top-left breadcrumb plan context (e.g. "Overview · Free Weekly"). Reflects
  // the REAL resolved plan group from /api/entitlements/me, not account_type.
  const planContext = (!isAdmin && ent.ready && !ent.is_admin)
    ? planContextLabel(ent.plan_group, ja)
    : null;
  const planPill = (() => {
    if (isAdmin) return null;
    if (isFreeWeeklyTier) return { label: ja ? '無料週次プラン' : 'Free Weekly', compact: ja ? '無料週次' : 'Free Weekly', Icon: CalendarClock, color: 'bg-violet-50 text-violet-700 border-violet-200' };
    if (isPaidPlan) return { label: ja ? 'プレミアム' : 'Premium', compact: ja ? 'プレミアム' : 'Premium', Icon: Crown, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
    // Iter 324 — Per host design directive 2026-05-25: do NOT show
    // "体験版" / "Prototype member" / "Trial active" framing in the
    // customer chrome. Every early-access customer is on the 1-Year
    // Premium Free Trial — we keep one calm "先行受付 / Early Access"
    // pill across all customer states so the dashboard doesn't feel
    // like a perpetual sales banner.
    return {
      label: ja ? '先行受付' : 'Early Access',
      compact: ja ? '先行受付' : 'Early Access',
      Icon: Sparkles,
      color: 'bg-violet-50 text-violet-700 border-violet-200',
    };
  })();
  // Iter 326 — Suite navigation must follow the SAME canonical signal the
  // backend enforces. Business customers who used the Account Type Switch
  // get account_type='business' (→ BUSINESS_SUITE entitlement + full
  // /business-suite/* access) but keep the legacy role='user'. Gating the
  // suite nav purely on role==='business_suite' meant those customers had
  // backend access yet NO way to discover the Business Suite from the
  // sidebar (orphaned routes reachable only by direct URL). We now also
  // honour the resolved plan_group from /entitlements/me.
  const isBusinessSuite = user?.role === 'business_suite'
    || (ent.ready && ent.plan_group === 'BUSINESS_SUITE');
  const isGovtMilitary = user?.role === 'govt_military_suite'
    || (ent.ready && ent.plan_group === 'GOVT_MILITARY_SUITE');
  const isSuiteUser = isBusinessSuite || isGovtMilitary;
  // While entitlements are still loading we render the full nav so the
  // sidebar doesn't flash empty. Server-side gates (403) are the real
  // guarantee — the sidebar is purely UX.
  // Iter 334i — DENY-BY-DEFAULT for entitlement-gated sidebar items.
  // Previously this returned true while entitlements were loading,
  // which let Business-only items (Bulk Reports, Vehicle Monitoring,
  // Manager Review) flash into a Personal customer's sidebar for the
  // ~200ms window before the entitlement payload arrived. Now items
  // without a `feature` requirement always show (Dashboard home,
  // Settings, etc.); items WITH a feature requirement only show once
  // entitlements have loaded and resolved as allowed.
  const canShow = (feature) =>
    !feature || (ent.ready && (ent.is_admin || ent.can(feature)));
  // Iter 325 — Customer sidebar simplification.
  // PRIMARY  → 5 items every customer needs every day.
  // MORE     → collapsible (closed by default). Things customers reach
  //            for occasionally without distracting new users.
  // Hidden from sidebar (still reachable via direct URL or
  // `/dashboard/advanced`): Consent, Referrals, JTR Pattern Review,
  // CC Recipients. They move to Settings → Advanced in a future sprint.
  // Personal customer IA — 7 clean groups. Items are entitlement/onboarding
  // gated below. Personal customers get a clean single sidebar with
  // hover-flyout submenus; Business/Gov get the two-level rail.
  const personalNavGroupsRaw = [
    { id: 'overview', label: ja ? '概要' : 'Overview', icon: LayoutDashboard, items: [
      { icon: LayoutDashboard, label: ja ? '概要' : 'Overview', path: '/dashboard' },
    ]},
    { id: 'meisai-setup', label: ja ? 'MEISAI設定' : 'MEISAI Setup', shortLabel: 'MEISAI', icon: Activity, items: [
      { icon: Database, label: ja ? 'MEISAI設定' : 'MEISAI Setup', path: '/dashboard/meisai-setup' },
      { icon: Activity, label: ja ? 'MEISAI接続状況' : 'MEISAI Connection', path: '/dashboard/meisai-health', feature: 'meisai_connection_health', locked: !onboardingComplete },
      { icon: KeyRound, label: ja ? 'MEISAIセキュアボルト' : 'MEISAI Secure Vault', path: '/dashboard/meisai-vault', feature: 'meisai_connection_health' },
    ]},
    { id: 'reports', label: ja ? 'レポート' : 'Reports', icon: FileText, items: [
      { icon: FileText, label: ja ? 'レポート' : 'Reports', path: '/dashboard/reports', locked: !onboardingComplete },
      { icon: Receipt, label: ja ? 'レポート履歴' : 'Report History', path: '/dashboard/reports/history', locked: !onboardingComplete },
      { icon: CalendarClock, label: ja ? '予約レポート' : 'Scheduled Reports', path: '/dashboard/schedules', locked: !onboardingComplete },
      { icon: FileBarChart, label: ja ? 'カスタム期間レポート' : 'Custom Reports', path: '/dashboard/custom-reports', feature: 'custom_date_reports', locked: !onboardingComplete },
      { icon: Receipt, label: ja ? '経費精算レポート' : 'Reimbursement', path: '/dashboard/reimbursement', feature: 'reimbursement_reports', locked: !onboardingComplete },
    ]},
    { id: 'cards-vehicles', label: ja ? 'ETCカード・車両' : 'ETC Cards & Vehicles', shortLabel: ja ? 'カード' : 'Cards', icon: CreditCard, items: [
      { icon: CreditCard, label: ja ? 'ETCカード' : 'ETC Cards', path: '/dashboard/cards', locked: !onboardingComplete },
      { icon: ListChecks, label: ja ? 'カード設定・紐付け' : 'Card Setup & Mapping', path: '/dashboard/card-mapping', locked: !onboardingComplete },
      { icon: ClipboardList, label: ja ? 'カード登録状況' : 'Registration Status', path: '/dashboard/card-registration-status', locked: !onboardingComplete },
      { icon: ShieldCheck, label: ja ? '家族登録車両' : 'Family Authorized Vehicles', path: '/dashboard/family-authorized-vehicles', feature: 'family_vehicle_list', locked: !onboardingComplete },
      { icon: Car, label: ja ? '車両モニタリング' : 'Vehicle Monitoring', path: '/dashboard/vehicle-monitoring', feature: 'vehicle_monitoring', locked: !onboardingComplete },
    ]},
    { id: 'delivery', label: ja ? '配信と通知' : 'Delivery & Notifications', shortLabel: ja ? '配信' : 'Delivery', icon: Bell, items: [
      { icon: Send, label: ja ? '配信設定' : 'Delivery Settings', path: '/dashboard/reports/delivery', locked: !onboardingComplete },
      { icon: Users, label: ja ? '配信先（受信者）' : 'Recipients', path: '/dashboard/recipients', locked: !onboardingComplete },
      { icon: Bell, label: ja ? 'スマートアラート' : 'Smart Alerts', path: '/dashboard/spend-alerts', feature: 'smart_spend_alerts', locked: !onboardingComplete },
    ]},
    { id: 'account', label: ja ? 'アカウント' : 'Account', icon: Settings, items: [
      { icon: Crown, label: ja ? 'プラン・体験' : 'Plan & Trial', path: '/dashboard/plan' },
      { icon: Settings, label: ja ? '設定' : 'Settings', path: '/dashboard/settings' },
      { icon: Gift, label: ja ? 'ご紹介' : 'Referrals', path: '/dashboard/referrals', locked: !onboardingComplete },
    ]},
    { id: 'help', label: ja ? 'ヘルプ' : 'Help', icon: LifeBuoy, items: [
      { icon: Headset, label: ja ? 'サポート' : 'Support', path: '/dashboard/support' },
    ]},
  ];

  // Business / Gov Suite keep the advanced two-level rail — compact Home area
  // + the existing suite operating areas.
  const suiteNavGroupsRaw = [
    {
      id: 'primary',
      label: ja ? 'ホーム' : 'Home',
      icon: LayoutDashboard,
      collapsible: false,
      items: [
        { icon: LayoutDashboard, label: ja ? '概要' : 'Overview', path: '/dashboard' },
        ...(onboardingComplete && meisaiConnected
          ? [
              { icon: Activity, label: ja ? 'MEISAI接続状況' : 'MEISAI Connection', path: '/dashboard/meisai-health', feature: 'meisai_connection_health' },
              { icon: KeyRound, label: ja ? 'MEISAIセキュアボルト' : 'MEISAI Secure Vault', path: '/dashboard/meisai-vault', feature: 'meisai_connection_health' },
            ]
          : []
        ),
        { icon: FileText, label: ja ? 'レポート' : 'Reports', path: '/dashboard/reports', locked: !onboardingComplete },
        { icon: CreditCard, label: ja ? 'ETCカード' : 'ETC Cards', path: '/dashboard/cards', locked: !onboardingComplete },
      ],
    },
      {
        id: 'suite-overview',
        label: isGovtMilitary
          ? (ja ? '官公庁・公共部門スイート' : 'Government & Public-Sector Suite')
          : (ja ? 'ビジネススイート' : 'BUSINESS SUITE'),
        icon: Building2,
        collapsible: true,
        items: [
          { icon: Building2, label: ja ? 'ビジネスホーム' : 'Business Home', path: '/dashboard/business-suite' },
          { icon: Gauge,     label: ja ? '運用ダッシュボード' : 'Operations', path: '/dashboard/business-suite/operations', badge: ja ? '新' : 'New' },
          ...(isGovtMilitary
            ? [{ icon: ShieldCheck, label: ja ? 'オーバーサイト概要' : 'Oversight Summary', path: '/dashboard/business-suite/government-oversight', badge: ja ? '新' : 'New' }]
            : [{ icon: Building2, label: ja ? 'フリート概況' : 'Fleet Snapshot', path: '/dashboard/business-suite/business-fleet', badge: ja ? '新' : 'New' }]),
          { icon: Database,  label: ja ? '設定ウィザード' : 'Setup Wizard', path: '/dashboard/business-suite/wizard', badge: ja ? '新' : 'New' },
          { icon: Layers,    label: ja ? 'MEISAIアカウント' : 'MEISAI Accounts', path: '/dashboard/business-suite/meisai' },
          { icon: Rocket,    label: ja ? 'フリート稼働センター' : 'Fleet Activation', path: '/dashboard/business-suite/fleet-activation', badge: ja ? '新' : 'New' },
          { icon: ListChecks, label: ja ? '稼働準備タスク' : 'Activation Tasks', path: '/dashboard/business-suite/activation-queue', badge: ja ? '新' : 'New' },
          { icon: ClipboardCheck, label: ja ? 'カード準備状況' : 'Card Readiness', path: '/dashboard/business-suite/card-readiness', badge: ja ? '新' : 'New' },
        ],
      },
      {
        id: 'suite-cards',
        label: ja ? 'カードと車両' : 'CARDS & VEHICLES',
        icon: CreditCard,
        collapsible: true,
        items: [
          { icon: CreditCard, label: ja ? 'ETCカード管理' : 'ETC Card Management', path: '/dashboard/business-suite/cards-hub', badge: ja ? '新' : 'New' },
          { icon: CreditCard, label: ja ? 'カード一覧' : 'All Cards', path: '/dashboard/business-suite/cards' },
          { icon: ListChecks, label: ja ? 'カード設定・紐付け' : 'Card Setup & Mapping', path: '/dashboard/card-mapping' },
          { icon: ListChecks, label: ja ? 'カード処理状況' : 'Card Job Status', path: '/dashboard/business-suite/card-jobs' },
          { icon: Upload, label: ja ? 'カード一括取り込み' : 'Card Bulk Import', path: '/dashboard/business-suite/bulk-import' },
          { icon: Car, label: ja ? '複数車両の利用記録' : 'Multi-vehicle records', path: '/dashboard/business-suite/vehicles', badge: ja ? '新' : 'New' },
          { icon: Car, label: ja ? '車両準備状況' : 'Vehicle Readiness', path: '/dashboard/business-suite/vehicle-readiness', badge: ja ? '新' : 'New' },
          { icon: Upload, label: ja ? '車両一括取込' : 'Vehicle Import', path: '/dashboard/business-suite/vehicle-import', badge: ja ? '新' : 'New' },
        ],
      },
      {
        id: 'suite-teams',
        label: ja ? 'チームとグループ' : 'TEAMS & GROUPS',
        icon: Users,
        collapsible: true,
        items: [
          { icon: Users,     label: ja ? 'グループ' : 'Groups', path: '/dashboard/business-suite/groups' },
          { icon: Network,   label: ja ? 'グループ管理・権限' : 'Group Management', path: '/dashboard/business-suite/group-management', badge: ja ? '新' : 'New' },
          { icon: Network,   label: ja ? 'グループ階層' : 'Group Hierarchy', path: '/dashboard/business-suite/hierarchy', badge: ja ? '新' : 'New' },
          { icon: ShieldCheck, label: ja ? '管理者ワークスペース' : 'Manager Workspace', path: '/dashboard/business-suite/group-manager', badge: ja ? '新' : 'New' },
          { icon: ClipboardCheck, label: ja ? '管理者レビュー' : 'Manager Review', path: '/dashboard/manager-review', feature: 'manager_review_queue' },
        ],
      },
      {
        id: 'suite-reports',
        label: ja ? 'レポートと配信' : 'REPORTS & DELIVERY',
        icon: BarChart3,
        collapsible: true,
        items: [
          { icon: BarChart3, label: ja ? '一括レポート' : 'Bulk Reports', path: '/dashboard/business-suite/reports', feature: 'bulk_reports' },
          { icon: CalendarClock, label: ja ? '予約レポート' : 'Scheduled Reports', path: '/dashboard/business-suite/scheduled-reports', badge: ja ? '新' : 'New' },
          { icon: FileBarChart, label: ja ? 'レポートセンター' : 'Report Center', path: '/dashboard/business-suite/report-center', badge: ja ? '新' : 'New' },
          { icon: FileBarChart, label: ja ? '配信ログ' : 'Delivery Log', path: '/dashboard/business-suite/report-transparency', badge: ja ? '新' : 'New' },
          { icon: Bell, label: ja ? '通知グループ' : 'Notification Groups', path: '/dashboard/business-suite/notifications', badge: ja ? '新' : 'New' },
          { icon: Bell, label: ja ? '配信先の確認' : 'Delivery Routes', path: '/dashboard/business-suite/notifications-visibility', badge: ja ? '新' : 'New' },
          { icon: Users, label: ja ? '配信先（受信者）' : 'Recipients', path: '/dashboard/recipients', feature: 'per_card_email_routing' },
        ],
      },
      {
        id: 'suite-monitoring',
        label: ja ? 'モニタリングとアラート' : 'MONITORING & ALERTS',
        icon: Bell,
        collapsible: true,
        items: [
          { icon: Bell,      label: ja ? 'スマートアラート' : 'Smart Alerts', path: '/dashboard/business-suite/alerts', feature: 'smart_spend_alerts' },
          { icon: ShieldCheck, label: ja ? '車両モニタリング' : 'Vehicle Monitoring', path: '/dashboard/vehicle-monitoring', feature: 'vehicle_monitoring' },
          { icon: Flag,      label: ja ? 'レッドフラグ' : 'Red Flags', path: '/dashboard/business-suite/red-flags', badge: ja ? '新' : 'New' },
          { icon: ListChecks, label: ja ? '処理状況' : 'Processing Status', path: '/dashboard/business-suite/queues', badge: ja ? '新' : 'New' },
        ],
      },
      {
        id: 'suite-account',
        label: ja ? 'アカウント' : 'ACCOUNT',
        icon: Wallet,
        collapsible: true,
        items: [
          { icon: Wallet, label: ja ? '請求プレビュー' : 'Billing Preview', path: '/dashboard/business-suite/card-billing', badge: ja ? '新' : 'New' },
          { icon: Headset, label: ja ? '専任アカウントマネージャー' : 'Account Manager', path: '/dashboard/business-suite/account-manager', badge: ja ? '新' : 'New' },
        ],
      },
  ];

  const customerNavGroupsRaw = isSuiteUser ? suiteNavGroupsRaw : personalNavGroupsRaw;

  // Apply entitlement filter to each group. Empty groups disappear entirely.
  // Iter 312 — During pre-ETC Usage Inquiry Service (MEISAI) onboarding we also hide every locked
  // OPERATIONS row so the customer sees ONLY: Overview · ETC Usage Inquiry Service (MEISAI) Setup.
  // Sidebar bloat was a top distraction. Locked rows return once
  // onboardingComplete flips true.
  const customerNavGroups = customerNavGroupsRaw
    .map((g) => ({
      ...g,
      items: g.items.filter((it) => {
        if (!canShow(it.feature)) return false;
        if (!onboardingComplete && it.locked) return false;
        return true;
      }),
    }))
    .filter((g) => g.items.length > 0);

  const isHost = user?.role === 'host';
  // Sectionalised admin nav — 6 consolidated hubs (was 10) so the sidebar
  // stays scannable while every built page stays discoverable. Each group has
  // a stable `id` for localStorage persistence of its accordion state. NOTE:
  // all routes are preserved — only the grouping/order changed.
  const adminNavGroups = [
    {
      id: 'overview',
      label: ja ? 'ダッシュボード' : 'Dashboard & Metrics',
      shortLabel: ja ? 'ダッシュ' : 'Dashboard',
      icon: LayoutDashboard,
      items: [
        { icon: Gauge, label: ja ? 'コマンドセンター' : 'Command Center', path: '/admin', highlight: true },
        { icon: Activity, label: ja ? 'レポート実績トラッカー' : 'Live Report Activity', path: '/admin/report-activity' },
        { icon: Rocket, label: ja ? 'ローンチ状況' : 'Launch Status', path: '/admin/launch-status' },
        { icon: LayoutDashboard, label: ja ? '指標・メトリクス' : 'All Metrics', path: '/admin/metrics' },
        { icon: Gauge, label: ja ? 'パフォーマンス・スケール' : 'Performance & Scale', path: '/admin/performance' },
      ],
    },
    {
      id: 'customers',
      label: ja ? '顧客・組織' : 'Customers & Orgs',
      shortLabel: ja ? '顧客' : 'Customers',
      icon: Users,
      items: [
        { icon: Users, label: t('sidebar.customers'), path: '/admin/customers' },
        { icon: Building2, label: ja ? '組織オペレーション' : 'Org Operations', path: '/admin/org-operations' },
        { icon: ShieldCheck, label: ja ? 'アカウント申請' : 'Account Requests', path: '/admin/account-type-requests' },
        { icon: Building2, label: ja ? '法人のお申し込み' : 'Business Applications', path: '/admin/business-applications' },
        { icon: Search, label: ja ? '顧客360診断' : 'Diagnostic 360', path: '/admin/diagnostic' },
        { icon: LifeBuoy, label: t('sidebar.support'), path: '/admin/support-messages' },
        { icon: Activity, label: ja ? '訪問者・サイトログ' : 'Visitors & Site Log', path: '/admin/visitors' },
        { icon: AlertTriangle, label: ja ? '閉鎖済みアカウント' : 'Closed Accounts', path: '/admin/closed-accounts' },
        { icon: Wallet, label: ja ? '請求プレビュー' : 'Billing Preview', path: '/admin/card-billing' },
      ],
    },
    {
      id: 'reports-ops',
      label: ja ? 'レポート・自動運用' : 'Reports & Ops',
      shortLabel: ja ? 'レポート' : 'Reports',
      icon: Activity,
      items: [
        { icon: FileText, label: t('sidebar.reports'), path: '/admin/reports' },
        { icon: AlertTriangle, label: ja ? '問題・リカバリー' : 'Issues & Recovery', path: '/admin/issues', highlight: true },
        ...(isHost ? [{ icon: CalendarClock, label: ja ? 'スケジューラ健全性' : 'Scheduler Health', path: '/admin/scheduler-health', hostOnly: true }] : []),
        { icon: Activity, label: ja ? '自動化設定' : 'Automation Center', path: '/admin/automation' },
        { icon: Wifi, label: t('sidebar.meisaiMonitor'), path: '/admin/meisai-monitor' },
        { icon: Activity, label: ja ? 'ジョブモニター' : 'Job Monitor', path: '/admin/jobs' },
        ...(isHost ? [{ icon: Inbox, label: ja ? 'メール配信ヘルス' : 'Email Delivery Health', path: '/admin/email-delivery-health', hostOnly: true }] : []),
        { icon: Database, label: t('sidebar.dataHealth'), path: '/admin/data-health' },
      ],
    },
    {
      id: 'security',
      label: ja ? 'セキュリティ・健全性' : 'Security & Health',
      shortLabel: ja ? 'セキュリティ' : 'Security',
      icon: Shield,
      items: [
        { icon: ShieldAlert, label: ja ? 'セキュリティ・コックピット' : 'Security Cockpit', path: '/admin/security' },
        { icon: Shield, label: t('sidebar.security'), path: '/admin/security-events' },
        { icon: Shield, label: ja ? '要確認利用モニタリング' : 'Fraud Watch', path: '/admin/fraud-watch' },
        { icon: ShieldAlert, label: ja ? '不正・乱用レビュー' : 'Abuse Review', path: '/admin/abuse-review' },
        { icon: FileText, label: ja ? '取扱履歴' : 'Audit Logs', path: '/admin/audit' },
        { icon: ShieldCheck, label: ja ? 'エクスポート監査' : 'Export Audit', path: '/admin/export-audit' },
        { icon: ClipboardCheck, label: ja ? 'カード登録の失敗' : 'Registration Attempts', path: '/admin/registration-attempts' },
      ],
    },
    {
      id: 'growth',
      label: ja ? 'グロース・コンテンツ' : 'Growth & Comms',
      shortLabel: ja ? 'グロース' : 'Growth',
      icon: Megaphone,
      items: [
        { icon: Sparkles, label: ja ? '先行受付待機リスト' : 'Early Access Waitlist', path: '/admin/early-access' },
        { icon: Send, label: ja ? 'メールキャンペーン' : 'Email Campaigns', path: '/admin/email-campaigns' },
        { icon: Mail, label: t('sidebar.templates'), path: '/admin/email-templates' },
        { icon: ImageIcon, label: ja ? 'OGプレビュー' : 'OG Preview', path: '/admin/og-preview' },
        { icon: ImageIcon, label: ja ? 'ホームページ素材' : 'Homepage Media', path: '/admin/homepage-media' },
        { icon: Languages, label: ja ? 'お客様の声・翻訳' : 'Testimonial Translations', path: '/admin/testimonial-translations' },
      ],
    },
    {
      id: 'system',
      label: ja ? 'システム・連携' : 'System & Federation',
      shortLabel: ja ? 'システム' : 'System',
      icon: Settings,
      items: [
        { icon: Wifi, label: ja ? '連携API（ETCカード）' : 'Federation (ETC Card)', path: '/admin/federation' },
        { icon: Activity, label: ja ? '同期ステータス（JEC→NoJo→JTR）' : 'Sync status (JEC→NoJo→JTR)', path: '/admin/sync-status' },
        { icon: Rocket, label: t('sidebar.launchChecklist'), path: '/admin/launch-checklist' },
        { icon: CreditCard, label: ja ? 'お支払い開始準備' : 'Billing Readiness', path: '/admin/billing-readiness' },
        { icon: Activity, label: ja ? 'ベータ運用' : 'Beta Ops', path: '/admin/beta-operations' },
        { icon: Rocket, label: ja ? 'ローンチ週運用' : 'Launch Week Ops', path: '/admin/launch-week-ops' },
        ...(isHost ? [{ icon: Play, label: t('sidebar.phaseBLive'), path: '/admin/phase-b-live', hostOnly: true }] : []),
        ...(isHost ? [{ icon: Activity, label: ja ? 'MEISAIライブ口座' : 'MEISAI Live Accounts', path: '/admin/meisai-live-accounts', hostOnly: true }] : []),
        { icon: BookOpen, label: ja ? 'ワークフロー手順書' : 'Workflow Playbook', path: '/admin/workflow-playbook', testId: 'sidebar-workflow-playbook' },
        { icon: Settings, label: t('sidebar.adminSettings'), path: '/admin/settings' },
        ...(isHost ? [{ icon: Crown, label: t('sidebar.hostTools'), path: '/admin/host-tools', hostOnly: true }] : []),
        ...(isHost ? [{ icon: FileText, label: ja ? '翻訳エクスポート' : 'Translation Exports', path: '/admin/exports', hostOnly: true }] : []),
      ],
    },
  ];
  // Flat list for active-route lookup + mobile rendering compat.
  const adminNav = adminNavGroups.flatMap(g => g.items);

  // Iter 314 — Use the UNFILTERED customer item list for active-page
  // lookup so the breadcrumb and topbar page label match the route
  // even when the sidebar is collapsed during pre-ETC Usage Inquiry Service (MEISAI) onboarding
  // (e.g. visiting /dashboard/settings while only Overview + ETC Usage Inquiry Service (MEISAI)
  // Setup are visible in the sidebar). The filtered `customerNavGroups`
  // continues to drive sidebar rendering only.
  //
  // Some routes are reachable only via the user dropdown (Settings,
  // Support, Billing) and therefore never appear in the sidebar. We
  // augment the unfiltered lookup with a small extras list so the
  // breadcrumb resolves correctly on those routes too.
  const extraCustomerRoutes = [
    { icon: Settings,    label: ja ? '設定' : 'Settings',                path: '/dashboard/settings' },
    { icon: HelpCircle,  label: ja ? 'サポート' : 'Support',              path: '/dashboard/support' },
    { icon: Receipt,     label: ja ? 'プラン・お支払い' : 'Plan & Billing', path: '/dashboard/billing' },
  ];
  const allCustomerItemsUnfiltered = [
    ...customerNavGroupsRaw.flatMap(g => g.items),
    ...extraCustomerRoutes,
  ];
  const allCustomerItems = customerNavGroups.flatMap(g => g.items);
  // Pick the MOST SPECIFIC (longest) matching path so a parent route doesn't
  // mask a child (e.g. /business-suite vs /business-suite/card-jobs). `isActive`
  // is a startsWith match, so a plain .find() would resolve to the parent.
  const pickByLongestActive = (items, fallback) => {
    let best = null;
    let bestLen = -1;
    for (const it of items) {
      if (isActive(it.path) && it.path.length > bestLen) { best = it; bestLen = it.path.length; }
    }
    return best || fallback;
  };
  const currentPage = isAdmin
    ? pickByLongestActive(adminNav, adminNav[0])
    : pickByLongestActive(allCustomerItemsUnfiltered, allCustomerItems[0]);

  // ── Unified two-column navigation model ──────────────────────────────
  // ── Single-column navigation model ───────────────────────────────────
  // EVERY signed-in persona (Personal, Free Weekly, Business, Government,
  // Admin, Host) uses the same NavSidebar (accordion when expanded, icon +
  // popover when collapsed). `navAreas` is already RBAC/onboarding-filtered
  // upstream, so access control carries through unchanged. Active-group
  // detection + accordion open-state now live inside NavSidebar itself.
  const navAreas = isAdmin ? adminNavGroups : customerNavGroups;

  // Close the account menu when clicking outside it.
  useEffect(() => {
    const onClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // (Submenu item rendering lives in ContextualSidebar.jsx.)

  return (
    <div data-app-shell="dashboard" className="min-h-screen bg-gradient-to-br from-violet-50/40 via-white to-blue-50/40 flex">
      {/* Desktop navigation — single-column accordion (expanded) / icon+popover
          (collapsed). Replaces the old two-column hover-flyout that truncated
          long bilingual labels and felt unstable. */}
      <div ref={navShellRef} data-testid="nav-two-level">
        <NavSidebar
          areas={navAreas}
          isActive={isActive}
          brandTo={isAdmin ? '/admin' : '/dashboard'}
          ja={ja}
          accent={isAdmin ? 'admin' : 'customer'}
          expanded={railExpanded}
          onToggleExpand={() => setRailExpanded((v) => !v)}
          onInfoClick={isAdmin ? undefined : () => setShowInfoModal(true)}
          persistKey={isAdmin ? 'nav-admin' : 'nav-customer'}
        />
      </div>

      {/* Main column — margin reflows for the two-column nav (80px rail +
          256px panel = 336px) or just the 80px rail when the panel is hidden. */}
      <div className={`flex-1 min-w-0 flex flex-col overflow-hidden ${
        railExpanded ? 'md:ml-[336px]' : 'md:ml-20'
      }`}>
        <LaunchCountdownBanner />
        {/* Sticky Top Header */}
        <header
          className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-gray-200/80 h-16 px-4 md:px-8 flex items-center justify-between flex-shrink-0"
          data-testid="dashboard-topbar"
        >
          {/* Left */}
          <div className="flex items-center gap-3 min-w-0">
            <button
              ref={menuBtnRef}
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden inline-flex items-center justify-center w-11 h-11 flex-shrink-0 text-slate-600 hover:bg-slate-100 rounded-lg"
              data-testid="mobile-sidebar-toggle"
              aria-label={ja ? 'メニュー' : 'Menu'}
              aria-expanded={mobileOpen}
              aria-controls="mobile-nav-drawer"
            >
              {mobileOpen ? <X className="w-5 h-5" strokeWidth={1.5} /> : <Menu className="w-5 h-5" strokeWidth={1.5} />}
            </button>
            <Link to={isAdmin ? '/admin' : '/dashboard'} className="md:hidden flex items-center" data-testid="mobile-brand">
              <img src="/assets/logo/japantollreceipts.gif" alt="JTR" className="h-8 w-auto object-contain" />
            </Link>
            {!isAdmin && planContext && (
              <span
                className="md:hidden inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-[11px] font-semibold text-slate-500 flex-shrink-0"
                data-testid="mobile-topbar-plan-context"
                title={planContext}
              >
                {/Premium|プレミアム/i.test(planContext) ? (ja ? 'プレミアム' : 'Premium')
                  : /Business|ビジネス/i.test(planContext) ? (ja ? 'ビジネス' : 'Business')
                  : /Government|政府|官公/i.test(planContext) ? (ja ? '官公庁' : 'Gov')
                  : planContext}
              </span>
            )}
            <div className="hidden md:flex items-center gap-2">
              <currentPage.icon className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
              <span className="text-sm font-semibold text-slate-800">{currentPage.label}</span>
              {!isAdmin && planContext && (
                <span className="inline-flex items-center text-sm text-slate-400" data-testid="topbar-plan-context">
                  <span className="mx-1.5" aria-hidden="true">·</span>
                  <span className="font-medium text-slate-500">{planContext}</span>
                </span>
              )}
            </div>
            {isAdmin && <EnvironmentRibbon compact />}
            {isAdmin && <SystemHealthBadge />}
          </div>

          {/* Right */}
          <div className="flex items-center gap-1.5">
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="topbar-view-site"
              title={ja ? '公開サイトを表示' : 'View public site'}
              className="hidden sm:inline-flex items-center justify-center gap-1.5 px-2.5 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 sm:py-1.5 flex-shrink-0 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
            >
              <Home className="w-4 h-4" strokeWidth={1.75} />
              <span className="hidden lg:inline text-xs font-semibold">{ja ? 'サイトを表示' : 'View site'}</span>
            </a>
            {isAdmin && (
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent('jtr:open-command-palette'))}
                data-testid="admin-mobile-search"
                title={ja ? '検索 (⌘K)' : 'Search (⌘K)'}
                aria-label={ja ? '検索' : 'Search'}
                className="sm:hidden inline-flex items-center justify-center min-h-[44px] min-w-[44px] flex-shrink-0 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
              >
                <Search className="w-4 h-4" strokeWidth={1.75} />
              </button>
            )}
            {!isAdmin && (
              <SetupProgressPill user={user} onboardingComplete={onboardingComplete} />
            )}
            {!isAdmin && (() => {
              // Iter 334b — Consolidated single "account & access" chip.
              // Previously this was three separate chips (Track / Plan /
              // Early Access). Customer feedback: opening three buttons
              // that all show the same modal is confusing. They are now
              // a single segmented control that renders the three states
              // side-by-side and opens one panel.
              const track = (user?.account_type || 'personal').toLowerCase();
              const TRACK_META = {
                personal:   { label_en: 'Personal',     label_ja: '個人',         pillBg: 'bg-violet-50 text-violet-700 border-violet-200',     Icon: Users },
                business:   { label_en: 'Business',     label_ja: '法人',         pillBg: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: Building2 },
                government: { label_en: 'Gov & Public.',   label_ja: '官公庁・公共部門',  pillBg: 'bg-sky-50 text-sky-700 border-sky-200',             Icon: ShieldCheck },
              }[track] || { label_en: 'Personal', label_ja: '個人', pillBg: 'bg-violet-50 text-violet-700 border-violet-200', Icon: Users };
              const segments = [
                {
                  id: 'track',
                  Icon: TRACK_META.Icon,
                  label: ja ? TRACK_META.label_ja : TRACK_META.label_en,
                  className: `${TRACK_META.pillBg} border-r`,
                  tooltip: ja
                    ? 'アカウントタイプで、個人・法人・官公庁向けツールの表示が決まります。'
                    : 'Account type controls whether Personal, Business, or Government & Public-Sector tools are available.',
                  testid: 'topbar-account-chip-track',
                },
                {
                  id: 'plan',
                  Icon: isFreeWeeklyTier ? CalendarClock : Crown,
                  label: isFreeWeeklyTier ? (ja ? '無料週次プラン' : 'Free Weekly') : (ja ? 'プレミアム' : 'Premium'),
                  className: isFreeWeeklyTier
                    ? 'bg-violet-50 text-violet-700 border-r border-violet-200'
                    : 'bg-slate-900 text-white border-r border-slate-900',
                  tooltip: isFreeWeeklyTier
                    ? (ja
                        ? '無料週次プランをご利用中です。週次レポート・PDF/CSV出力・基本的なMEISAI連携をご利用いただけます。'
                        : "You're on the Free Weekly plan — weekly reports, PDF/CSV exports, and basic MEISAI access.")
                    : (ja
                        ? 'プレミアム機能の利用レベルです。先行受付期間中は追加請求なしで利用できます。'
                        : 'Premium is your feature level during Early Access — no billing is shown or charged.'),
                  testid: 'topbar-account-chip-plan',
                },
                {
                  id: 'access',
                  Icon: Sparkles,
                  label: isFreeWeeklyTier
                    ? (ja ? '週次レポートのみ' : 'Weekly Reports Only')
                    : (ja ? '先行受付・1年無料' : 'Early Access · 1-yr free'),
                  compactLabel: isFreeWeeklyTier
                    ? (ja ? '週次のみ' : 'Weekly only')
                    : (ja ? '先行受付' : 'Early Access'),
                  className: isFreeWeeklyTier
                    ? 'bg-violet-100 text-violet-800'
                    : 'bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white',
                  tooltip: isFreeWeeklyTier
                    ? (ja
                        ? '日次レポート・スマートアラート・カスタムレポート・複数車両の利用記録はプレミアムプランで利用可能です。'
                        : 'Daily Reports, Smart Alerts, Custom Reports, and Vehicle Monitoring are available on Premium.')
                    : (ja
                        ? '先行受付メンバーとして、1年間プレミアム機能をご利用いただけます。'
                        : 'Early Access gives this account 1 year of Premium features at no cost.'),
                  testid: 'topbar-account-chip-access',
                },
              ];
              return (
                <TooltipProvider delayDuration={120}>
                  <div
                    className="hidden lg:inline-flex items-center gap-0 rounded-full border border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm transition overflow-hidden"
                    role="group"
                    aria-label={ja ? 'アカウントとご利用状況' : 'Your account & access'}
                    data-testid="topbar-account-chip"
                  >
                    {segments.map((segment) => (
                      <Tooltip key={segment.id}>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={() => setAccessPanel(segment.id)}
                            className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9200C7] focus-visible:ring-offset-1 ${segment.className}`}
                            data-testid={segment.testid}
                          >
                            <segment.Icon className="w-3 h-3" strokeWidth={2.2} />
                            {segment.id === 'access' ? (
                              <>
                                <span className="hidden lg:inline">{segment.label}</span>
                                <span className="lg:hidden">{segment.compactLabel}</span>
                              </>
                            ) : segment.label}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" data-testid={`${segment.testid}-tooltip`}>
                          {segment.tooltip}
                        </TooltipContent>
                      </Tooltip>
                    ))}
                  </div>
                </TooltipProvider>
              );
            })()}

            {/* Iter 311 — Customer-facing "Plans" upsell hidden during the
                1-Year Premium Free Trial early-access phase. Billing
                infrastructure remains intact server-side. */}

            <div className="flex items-center bg-purple-50 border border-purple-200 rounded-full p-0.5 mx-1 shadow-sm" data-testid="topbar-lang-toggle">
              <button
                onClick={() => setLang('ja', { persist: true })}
                className={`inline-flex items-center justify-center min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 px-2.5 py-1 text-[11px] font-bold rounded-full transition-colors ${lang === 'ja' ? 'bg-[#9200C7] text-white shadow-sm' : 'text-[#9200C7] hover:bg-purple-100'}`}
                data-testid="topbar-lang-ja"
                aria-label={ja ? '日本語に切り替え' : 'Switch to Japanese'}
                aria-pressed={lang === 'ja'}
              >JA</button>
              <button
                onClick={() => setLang('en', { persist: true })}
                className={`inline-flex items-center justify-center min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 px-2.5 py-1 text-[11px] font-bold rounded-full transition-colors ${lang === 'en' ? 'bg-[#9200C7] text-white shadow-sm' : 'text-[#9200C7] hover:bg-purple-100'}`}
                data-testid="topbar-lang-en"
                aria-label={ja ? 'Switch to English' : 'Switch to English'}
                aria-pressed={lang === 'en'}
              >EN</button>
            </div>

            <Link
              to={isAdmin ? '/admin/support-messages' : '/dashboard/support'}
              className="hidden md:flex p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              title={ja ? 'サポート' : 'Support'}
              data-testid="topbar-support"
            >
              <HelpCircle className="w-[18px] h-[18px]" strokeWidth={1.5} />
            </Link>
            {isAdmin && (
              <Link
                to="/admin/workflow-playbook"
                className="hidden md:flex p-2 text-slate-400 hover:text-violet-700 hover:bg-violet-50 rounded-lg transition-colors"
                title="GitHub & Agent Workflow Playbook"
                data-testid="topbar-workflow-playbook"
                aria-label="Open GitHub and agent workflow playbook"
              >
                <BookOpen className="w-[18px] h-[18px]" strokeWidth={1.75} />
              </Link>
            )}
            {(() => {
              const helpKey = resolveHelpKey(location.pathname);
              return helpKey ? (
                <div className="hidden md:flex items-center" data-testid="topbar-pagehelp">
                  <PageHelp pageKey={helpKey} />
                </div>
              ) : null;
            })()}
            {isAdmin && <AdminThemeSwitcher />}
            {isAdmin && <SelfRescueInboxPill ja={ja} />}
            {isAdmin && <JecNojoJtrSyncPill ja={ja} />}

            <div
              className="relative ml-1"
              ref={userMenuRef}
              onMouseEnter={() => { if (isHoverCapable()) openUserMenu(); }}
              onMouseLeave={() => { if (isHoverCapable()) scheduleCloseUserMenu(); }}
            >
              <button
                onClick={() => setUserMenuOpen(o => !o)}
                className="flex items-center justify-center gap-2 pl-1.5 pr-2 min-h-[44px] sm:min-h-0 sm:py-1 rounded-full hover:bg-slate-100 transition-colors"
                data-testid="topbar-user-menu"
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
              >
                <span className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-white text-xs font-bold flex items-center justify-center">
                  {initials}
                </span>
                <ChevronDown className="w-3.5 h-3.5 text-slate-500 hidden sm:block" strokeWidth={2} />
              </button>
              {userMenuOpen && (
                <div
                  className="absolute right-0 top-full mt-1 w-60 bg-white border border-slate-200 rounded-xl shadow-lg py-2 z-50"
                  data-testid="topbar-user-dropdown"
                >
                  <div className="px-4 py-2 border-b border-slate-100">
                    <p className="text-sm font-semibold text-slate-900 truncate">{displayName}</p>
                    <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                    {user?.user_id_display && !isAdmin && (
                      <p className="text-[10px] font-bold text-violet-600 mt-1" data-testid="topbar-user-jtr-id">{user.user_id_display}</p>
                    )}
                    {isAdmin && (
                      <span className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold border border-violet-200 bg-violet-50 text-violet-700" data-testid="topbar-role-pill">
                        {isHost ? t('role.host') : t('role.admin')}
                      </span>
                    )}
                    {planPill && !isAdmin && (
                      <span className={`inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold border ${planPill.color}`}>
                        <planPill.Icon className="w-3 h-3" strokeWidth={2} /> {planPill.label}
                      </span>
                    )}
                  </div>

                  {isAdmin ? (
                    <>
                      <Link to="/admin" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50" data-testid="topbar-admin-dashboard">
                        <LayoutDashboard className="w-4 h-4" strokeWidth={1.5} /> {t('topMenu.adminDashboard')}
                      </Link>
                      <Link to="/admin/settings" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50" data-testid="topbar-admin-settings">
                        <Settings className="w-4 h-4" strokeWidth={1.5} /> {t('topMenu.adminSettings')}
                      </Link>
                      {isHost && (
                        <Link to="/admin/host-tools" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50" data-testid="topbar-host-tools">
                          <Crown className="w-4 h-4" strokeWidth={1.5} /> {t('topMenu.hostTools')}
                        </Link>
                      )}
                    </>
                  ) : (
                    <>
                      <Link to="/dashboard/settings" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50" data-testid="topbar-customer-settings">
                        <Settings className="w-4 h-4" strokeWidth={1.5} /> {t('topMenu.customerSettings')}
                      </Link>
                      {/* Iter 311 — "Billing & Plans" link hidden from
                          customer menu during early-access trial. */}
                      <Link to="/dashboard/support" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
                        <HelpCircle className="w-4 h-4" strokeWidth={1.5} /> {ja ? 'サポート' : 'Support'}
                      </Link>
                      <a
                        href="/"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                        data-testid="topbar-back-to-home"
                      >
                        <Home className="w-4 h-4" strokeWidth={1.5} />
                        <span className="flex-1">{ja ? 'ホームへ戻る' : 'Back to Home'}</span>
                        <ExternalLink className="w-3 h-3 text-slate-400" strokeWidth={2} />
                      </a>
                    </>
                  )}

                  <div className="border-t border-slate-100 mt-1 pt-1">
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      data-testid="topbar-logout"
                    >
                      <LogOut className="w-4 h-4" strokeWidth={1.5} /> {t('topMenu.logout')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Mobile drawer — clean light slide-out (role-adaptive sections) */}
        {mobileOpen && (
          <div className="md:hidden fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-overlay">
            <div
              ref={drawerRef}
              id="mobile-nav-drawer"
              role="dialog"
              aria-modal="true"
              aria-label={ja ? 'ナビゲーションメニュー' : 'Navigation menu'}
              tabIndex={-1}
              className="w-[88vw] max-w-xs h-full bg-white shadow-2xl overflow-y-auto flex flex-col outline-none motion-safe:animate-in motion-safe:slide-in-from-left motion-safe:duration-200"
              style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
              onClick={e => e.stopPropagation()}
              data-testid="mobile-nav-drawer"
            >
              {/* Header: brand + close */}
              <div className="h-16 px-4 flex items-center justify-between border-b border-slate-100 flex-shrink-0">
                <Link to={isAdmin ? '/admin' : '/dashboard'} onClick={() => setMobileOpen(false)} className="flex items-center">
                  <img src="/assets/logo/japantollreceipts.gif" alt="JTR" className="h-8 w-auto object-contain" />
                </Link>
                <button
                  onClick={() => setMobileOpen(false)}
                  className="inline-flex items-center justify-center w-11 h-11 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                  data-testid="mobile-nav-close"
                  aria-label={ja ? '閉じる' : 'Close'}
                >
                  <X className="w-5 h-5" strokeWidth={2} />
                </button>
              </div>

              {/* Current page / section breadcrumb */}
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/60 flex-shrink-0">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">{ja ? '現在のページ' : 'Current page'}</p>
                <div className="flex items-center gap-2 mt-1">
                  <currentPage.icon className="w-4 h-4 text-[#9200C7] flex-shrink-0" strokeWidth={2} />
                  <span className="text-sm font-bold text-slate-900 truncate">{currentPage.label}</span>
                  {!isAdmin && planContext && (
                    <span className="ml-auto flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full bg-[#9200C7]/10 text-[10px] font-bold text-[#9200C7]" data-testid="mobile-plan-context">
                      {/Premium|プレミアム/i.test(planContext) ? (ja ? 'プレミアム' : 'Premium')
                        : /Business|ビジネス/i.test(planContext) ? (ja ? 'ビジネス' : 'Business')
                        : /Government|政府|官公/i.test(planContext) ? (ja ? '官公庁' : 'Gov')
                        : planContext}
                    </span>
                  )}
                </div>
              </div>

              {/* Language toggle */}
              <div className="px-4 pt-4">
                <div className="inline-flex items-center bg-slate-100 rounded-full p-0.5" data-testid="sidebar-lang-toggle">
                  <button
                    onClick={() => setLang('ja', { persist: true })}
                    className={`px-3 py-1.5 text-[11px] font-bold rounded-full transition-colors ${lang === 'ja' ? 'bg-[#9200C7] text-white shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                    data-testid="sidebar-lang-ja"
                  >日本語</button>
                  <button
                    onClick={() => setLang('en', { persist: true })}
                    className={`px-3 py-1.5 text-[11px] font-bold rounded-full transition-colors ${lang === 'en' ? 'bg-[#9200C7] text-white shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                    data-testid="sidebar-lang-en"
                  >English</button>
                </div>
              </div>

              {/* Nav sections */}
              <nav className="flex-1 py-4 px-3 space-y-5" data-testid="mobile-nav-sections">
                {navAreas.map(group => (
                  <div key={group.id || group.label} className="space-y-0.5">
                    <p className="px-3 mb-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 flex items-center gap-1.5">
                      {group.icon && <group.icon className="w-3.5 h-3.5" strokeWidth={2} />}{group.label}
                    </p>
                    {group.items.map((item, itemIdx) => item.locked ? (
                      <div key={`${item.path}#${itemIdx}`} className="flex items-center justify-between px-3 py-3 rounded-xl opacity-50 cursor-not-allowed select-none">
                        <div className="flex items-center gap-3">
                          <item.icon className="w-[18px] h-[18px] text-slate-400" strokeWidth={1.75} />
                          <span className="text-sm text-slate-500">{item.label}</span>
                        </div>
                        <Lock className="w-3.5 h-3.5 text-slate-400" strokeWidth={2} />
                      </div>
                    ) : (
                      <Link
                        key={`${item.path}#${itemIdx}`} to={item.path} onClick={() => setMobileOpen(false)}
                        data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`flex items-center gap-3 px-3 py-3 rounded-xl text-sm transition-colors ${
                          isActive(item.path) ? 'bg-[#9200C7]/[0.08] text-[#9200C7] font-semibold' : 'text-slate-700 hover:bg-slate-50 font-medium'
                        }`}
                      >
                        <item.icon className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.75} />
                        <span className="flex-1 truncate">{item.label}</span>
                        {item.hostOnly && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">HOST</span>}
                      </Link>
                    ))}
                  </div>
                ))}
              </nav>

              {/* Free Weekly plan chip (self-gating) */}
              <div className="px-4 pt-3 flex-shrink-0">
                <PlanChip />
              </div>

              {/* Footer: identity + logout */}
              <div className="p-4 border-t border-slate-100 flex items-center gap-3 flex-shrink-0">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                  {initials}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-slate-800 truncate">{displayName}</p>
                  <button onClick={() => { handleLogout(); setMobileOpen(false); }} className="text-[11px] text-red-600 hover:text-red-700 flex items-center gap-1 mt-0.5" data-testid="mobile-nav-logout">
                    <LogOut className="w-3 h-3" strokeWidth={1.5} /> {ja ? 'ログアウト' : 'Logout'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <main id="main-content" className="flex-1 min-w-0">
          {/* Top-of-content service-error banner — visible to logged-in
              users (admin + customer) whenever the backend starts
              returning 5xx or network errors. Auto-hides after 30s of
              no new errors. */}
          <ServiceErrorBanner />
          <div className={`px-4 sm:px-6 lg:px-8 lg:pr-20 pt-6 max-w-7xl mx-auto w-full ${isAdmin ? 'pb-24 lg:pb-6' : 'pb-20 lg:pb-6'}`}>
            {!isAdmin && user && <BusinessTrackNudge user={user} />}
            {!isAdmin && user && (
              <>
                {/* Iter 311 — Early-access phase: hide trial-expiry and
                    billing-status banners. Every customer is on the
                    1-Year Premium Free Trial; payment surfaces are
                    suppressed to avoid confusion. Backend Stripe
                    infrastructure remains untouched. */}
              </>
            )}
            {willRedirectToWizard ? (
              // Iter 394b — Prevent the "old Overview flashes for a frame
              // before the redirect fires" flicker. While we KNOW the
              // gate is about to bounce the customer to /dashboard/onboarding,
              // render a calm loader instead of the regular Outlet.
              <div
                className="flex items-center justify-center py-24"
                data-testid="dashboard-redirect-loader"
                role="status"
                aria-live="polite"
              >
                <div className="flex flex-col items-center gap-3 text-slate-500">
                  <div className="w-6 h-6 rounded-full border-2 border-slate-200 border-t-violet-500 animate-spin" />
                  <p className="text-xs font-medium">
                    {ja ? 'セットアップを再開しています…' : 'Resuming your setup…'}
                  </p>
                </div>
              </div>
            ) : (
              <ProductionGuardProvider>
                {(user?.role === 'admin' || user?.role === 'host') && <GlobalPausedBanner />}
                <Outlet context={{ subscription, meisaiStatus, cardsCount, onboardingComplete }} />
              </ProductionGuardProvider>
            )}
          </div>
        </main>
        {/* Admin keeps the deep customer-360 search on its floating button.
            Gated by actual role (admin/host) — never path alone — so a regular
            user can never see the customer search, even on an /admin path. */}
        {isAdmin && (user?.role === 'admin' || user?.role === 'host') && <AdminCommandPalette isHost={isHost} />}
      </div>

      {/* "How we handle your information" modal */}
      {showInfoModal && (
        <div
          className="fixed inset-0 z-[70] bg-slate-900/60 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200"
          data-testid="info-handling-modal"
          onClick={() => setShowInfoModal(false)}
        >
          <div
            className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-200 my-4 motion-safe:animate-in motion-safe:zoom-in-95 motion-safe:duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 sm:p-7">
              <div className="flex items-start gap-3 mb-5">
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center flex-shrink-0 shadow-md shadow-violet-500/30">
                  <ShieldCheck className="w-5 h-5 text-white" strokeWidth={2.25} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-slate-900">{ja ? '情報の取り扱い' : 'How we handle your information'}</h3>
                  <p className="text-xs text-slate-500 mt-0.5">{ja ? 'Japan Toll Receipts情報取り扱いポリシー' : 'Japan Toll Receipts information handling policy'}</p>
                </div>
                <button
                  onClick={() => setShowInfoModal(false)}
                  className="flex-shrink-0 p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                  data-testid="info-handling-close"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" strokeWidth={2} />
                </button>
              </div>

              <div className="space-y-4 text-sm text-slate-700 leading-relaxed">
                {ja ? (
                  <>
                    <div className="flex items-start gap-3"><span className="flex-shrink-0 w-6 h-6 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center text-xs font-bold">1</span><p>Japan Toll Receiptsでは、お客様が許可したETC利用照会サービス（MEISAI）接続情報を、設定・接続維持・レポート配信のためにのみ使用します。</p></div>
                    <div className="flex items-start gap-3"><span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-bold">2</span><p>お客様の接続情報は安全に暗号化され、お客様のアカウントに紐づけられます。</p></div>
                    <div className="flex items-start gap-3"><span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">3</span><p>通行明細データはパススルー方式で処理されます。詳細な通行データはレポート作成・配信のために使用され、ダッシュボード上には保存されません。</p></div>
                    <div className="flex items-start gap-3"><span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold">4</span><p><strong>重要：</strong> ETC利用照会サービス（MEISAI）のパスワードや確認コードは、安全な設定フォーム内でのみ入力してください。メール、チャット、サポートメッセージでは送信しないでください。</p></div>
                    <div className="flex items-start gap-3"><span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold">5</span><p>Japan Toll Receiptsは、公式ETC利用照会サービス（MEISAI）サイトまたは道路事業者ではありません。本サービスは、お客様が許可したレポート情報を整理・配信するための補助サービスです。</p></div>
                  </>
                ) : (
                  <>
                    <div className="flex items-start gap-3"><span className="flex-shrink-0 w-6 h-6 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center text-xs font-bold">1</span><p>Japan Toll Receipts uses your authorized ETC Usage Inquiry Service (MEISAI) connection information only to support setup, connection maintenance, and report delivery.</p></div>
                    <div className="flex items-start gap-3"><span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-bold">2</span><p>Your connection details are securely encrypted and linked to your account.</p></div>
                    <div className="flex items-start gap-3"><span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">3</span><p>Toll report data is handled through a pass-through delivery process. Detailed toll usage data is used to generate and deliver your report, then it is not stored on your dashboard.</p></div>
                    <div className="flex items-start gap-3"><span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold">4</span><p><strong>Important:</strong> Only enter ETC Usage Inquiry Service (MEISAI) passwords or verification codes inside the secure setup forms. Do not send passwords or verification codes by email, chat, or support message.</p></div>
                    <div className="flex items-start gap-3"><span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold">5</span><p>Japan Toll Receipts is not the official ETC Usage Inquiry Service (MEISAI) website or a toll road operator. This service helps organize and deliver authorized report information for your convenience.</p></div>
                  </>
                )}
              </div>

              <div className="mt-6 pt-5 border-t border-slate-100">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs mb-4">
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-700 font-semibold hover:underline" data-testid="info-privacy-link">{ja ? 'プライバシーポリシー' : 'Privacy Policy'}</a>
                  <span className="text-slate-300">·</span>
                  <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-700 font-semibold hover:underline" data-testid="info-terms-link">{ja ? '利用規約' : 'Terms of Service'}</a>
                  <span className="text-slate-300">·</span>
                  <a href="/legal/refund-policy" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-700 font-semibold hover:underline" data-testid="info-refund-link">{ja ? '返金ポリシー' : 'Refund Policy'}</a>
                  <span className="text-slate-300">·</span>
                  <a href="/legal/commercial-transactions" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-700 font-semibold hover:underline" data-testid="info-commercial-link">{ja ? '特定商取引法' : 'Commercial Transactions'}</a>
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={() => setShowInfoModal(false)}
                    className="px-5 py-2 text-sm font-bold text-white bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 rounded-lg transition-colors shadow-md shadow-violet-500/30"
                    data-testid="info-handling-confirm"
                  >
                    {ja ? 'わかりました' : 'Got it'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Iter 334 — FounderMemberBadge removed from the customer
          dashboard surface. The topbar already carries the Early
          Access chip; repeating it as a floating bottom-left badge
          (with the customer email + JTR ID baked in) was clutter and
          re-introduced the identity block we explicitly removed in
          Iter 332. The badge component still exists for the marketing
          site, just not surfaced here. */}

      <AccountAccessPanel
        open={!!accessPanel}
        focus={accessPanel}
        onClose={() => setAccessPanel(null)}
      />

    </div>
  );
}
